import type { ReactNode } from 'react';
import { App, Card, Skeleton, Switch, Typography } from 'antd';
import { CheckCircleOutlined, MailOutlined, PauseCircleOutlined, UserAddOutlined } from '@ant-design/icons';
import { PageHeader } from '../../components/PageHeader';
import { LoadError } from '../../components/states';
import { CardTitle } from '../../components/StatCard';
import { errorMessage } from '../../api/client';
import { useSettings, useUpdateSettings, type Settings } from '../../features/settings/api';
import { palette } from '../../app/theme';

type NotificationKey = keyof Settings['notifications'];
type Row = { key: Exclude<NotificationKey, 'email'>; title: string; icon: ReactNode; description: string };

const TASK_EMAILS: Row[] = [
  {
    key: 'taskAssigned',
    title: 'Task assigned',
    icon: <UserAddOutlined />,
    description: 'Emails a user when someone else assigns them a task. Assigning a task to yourself sends nothing.',
  },
  {
    key: 'taskCompleted',
    title: 'Task completed',
    icon: <CheckCircleOutlined />,
    description: 'Emails the person who assigned a task when someone else marks it complete.',
  },
];

const SENDING_EMAILS: Row[] = [
  {
    key: 'domainPaused',
    title: 'Sending paused',
    icon: <PauseCircleOutlined />,
    description: 'Emails everyone when a domain’s bounce or spam complaint rate pauses its campaigns and warm-up emails (Settings → Email warm-up).',
  },
];

/** Workspace-wide switches for the emails the CRM sends its users; each switch saves right away. */
export function NotificationsPage() {
  const { message } = App.useApp();
  const settings = useSettings();
  const update = useUpdateSettings();
  const n = settings.data?.notifications;

  const toggle = async (key: NotificationKey, on: boolean, label: string) => {
    if (!n) return;
    try {
      await update.mutateAsync({ notifications: { ...n, [key]: on } });
      message.success(`${label} ${on ? 'turned on' : 'turned off'}`);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };
  const busy = (key: NotificationKey) => update.isPending && update.variables?.notifications?.[key] !== n?.[key];

  return (
    <div className="page page-narrow">
      <PageHeader title="Notifications" subtitle="Emails the CRM sends to its users when something needs their attention. These apply to everyone in the workspace." />
      {settings.isError ? (
        <Card>
          <LoadError error={settings.error} onRetry={settings.refetch} title="Could not load settings" />
        </Card>
      ) : !n ? (
        <Card>
          <Skeleton active />
        </Card>
      ) : (
        <>
          <Card styles={{ body: { padding: 0 } }}>
            <SettingRow icon={<MailOutlined />} title="Email notifications" description="Off stops every notification email on this page. Invites and password resets are always sent." first>
              <Switch aria-label="Email notifications" checked={n.email} loading={busy('email')} disabled={update.isPending} onChange={(on) => void toggle('email', on, 'Email notifications')} />
            </SettingRow>
          </Card>
          <Card style={{ marginTop: 24 }} title={<CardTitle title="Tasks" subtitle={n.email ? 'Emails about task assignments' : 'Turn on email notifications above to send these'} />} styles={{ body: { padding: 0 } }}>
            {TASK_EMAILS.map((r, i) => (
              <SettingRow key={r.key} icon={r.icon} title={r.title} description={r.description} first={i === 0} muted={!n.email}>
                <Switch aria-label={r.title} checked={n[r.key]} loading={busy(r.key)} disabled={!n.email || update.isPending} onChange={(on) => void toggle(r.key, on, `${r.title} emails`)} />
              </SettingRow>
            ))}
          </Card>
          <Card style={{ marginTop: 24 }} title={<CardTitle title="Email sending" subtitle={n.email ? 'Emails about your sending domains' : 'Turn on email notifications above to send these'} />} styles={{ body: { padding: 0 } }}>
            {SENDING_EMAILS.map((r, i) => (
              <SettingRow key={r.key} icon={r.icon} title={r.title} description={r.description} first={i === 0} muted={!n.email}>
                <Switch aria-label={r.title} checked={n[r.key]} loading={busy(r.key)} disabled={!n.email || update.isPending} onChange={(on) => void toggle(r.key, on, `${r.title} emails`)} />
              </SettingRow>
            ))}
          </Card>
        </>
      )}
    </div>
  );
}

function SettingRow({ icon, title, description, first, muted, children }: { icon: ReactNode; title: string; description: string; first?: boolean; muted?: boolean; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', padding: '20px 24px', borderTop: first ? undefined : `1px solid ${palette.border}`, opacity: muted ? 0.6 : 1 }}>
      <span style={{ fontSize: 18, color: palette.textSecondary, paddingTop: 2 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Typography.Text strong style={{ display: 'block' }}>{title}</Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>{description}</Typography.Text>
      </div>
      {children}
    </div>
  );
}
